import AddIcon from '@mui/icons-material/Add';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'react-use';
import { isAdmin } from '../../../config/roles';
import { getAllInvoices } from '../../../data/api/incomes/InvoiceRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import CreateInvoiceModal from './CreateInvoiceModal';
import InvoiceCard from './InvoiceCard';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.14)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a' },
    },
};

const INVOICE_TYPES = ['FACTURA', 'NOTA_CREDITO', 'NOTA_DEBITO', 'NO_FACTURA', 'PRUEBA'];
const INVOICE_ORIGINS = ['Bingo', 'Compra', 'Donacion', 'Contrato'];
// Sistema que originó la transacción (no método de pago).
const TRANSACTION_ORIGINS = ['WOMPI', 'PSE', 'FENABOCOL'];
const TRANSACTION_RESPONSES = ['APPROVED', 'DECLINED', 'PENDING', 'VOIDED', 'ERROR'];

const Incomes = () => {
    const { auth, token } = useIndex();
    const admin = isAdmin(auth);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [meta, setMeta] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [load, setLoad] = useState(true);
    const [openCreate, setOpenCreate] = useState(false);

    const [search, setSearch] = useState('');
    const [invoiceType, setInvoiceType] = useState('');
    const [invoiceOrigin, setInvoiceOrigin] = useState('');
    const [transactionOrigin, setTransactionOrigin] = useState('');
    const [transactionResponse, setTransactionResponse] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [, cancelSearch] = useDebounce(() => {
        setPage(1);
        loadData(1, perPage);
    }, 700, [search]);

    useEffect(() => {
        cancelSearch();
    }, []);

    useEffect(() => {
        loadData(page, perPage);
    }, [page, perPage, invoiceType, invoiceOrigin, transactionOrigin, transactionResponse, dateFrom, dateTo]);

    const buildParams = (p, pp) => {
        const params = { page: p, per_page: pp };
        if (search) params.search = search;
        if (invoiceType) params.invoice_type = invoiceType;
        if (invoiceOrigin) params.invoice_origin = invoiceOrigin;
        if (transactionOrigin) params.transaction_origin = transactionOrigin;
        if (transactionResponse) params.transaction_response = transactionResponse;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        return params;
    };

    const loadData = async (p = page, pp = perPage) => {
        setLoad(true);
        try {
            const { data } = await getAllInvoices(token, buildParams(p, pp));
            if (data?.success) {
                setInvoices(data?.data ?? []);
                setMeta(data?.meta ?? null);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(false);
        }
    };

    const handleChangePage = (_e, newPage) => setPage(newPage);

    const handleChangePerPage = (e) => {
        setPerPage(+e.target.value);
        setPage(1);
    };

    const clearFilters = () => {
        setSearch('');
        setInvoiceType('');
        setInvoiceOrigin('');
        setTransactionOrigin('');
        setTransactionResponse('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
    };

    return (
        <PageContainer maxWidth={false}>
            <PageHeader>
                <Box className="title-wrap">
                    <Box className="icon-box">
                        <ReceiptLongOutlinedIcon />
                    </Box>
                    <Box>
                        <Typography className="title">Facturas / Ingresos</Typography>
                        <Typography className="subtitle">
                            Consulta el historial de facturas emitidas y crea nuevas manualmente.
                        </Typography>
                    </Box>
                </Box>
            </PageHeader>

            <FiltersCard>
                <FiltersGrid>
                    <TextField
                        size="small"
                        placeholder="Buscar referencia, descripción, usuario..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ ...fieldStyles, gridColumn: '1 / -1' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                            label="Tipo"
                            value={invoiceType}
                            onChange={(e) => setInvoiceType(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {INVOICE_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Origen</InputLabel>
                        <Select
                            label="Origen"
                            value={invoiceOrigin}
                            onChange={(e) => setInvoiceOrigin(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {INVOICE_ORIGINS.map((o) => (
                                <MenuItem key={o} value={o}>{o}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Sistema</InputLabel>
                        <Select
                            label="Sistema"
                            value={transactionOrigin}
                            onChange={(e) => setTransactionOrigin(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {TRANSACTION_ORIGINS.map((o) => (
                                <MenuItem key={o} value={o}>{o}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Estado</InputLabel>
                        <Select
                            label="Estado"
                            value={transactionResponse}
                            onChange={(e) => setTransactionResponse(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {TRANSACTION_RESPONSES.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="date"
                        label="Desde"
                        InputLabelProps={{ shrink: true }}
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        sx={fieldStyles}
                    />

                    <TextField
                        size="small"
                        type="date"
                        label="Hasta"
                        InputLabelProps={{ shrink: true }}
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        sx={fieldStyles}
                    />

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Por página</InputLabel>
                        <Select
                            label="Por página"
                            value={perPage}
                            onChange={handleChangePerPage}
                        >
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={30}>30</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                </FiltersGrid>

                <Box className="filters-header">
                    <Box className="filters-info">
                        <Chip label={`Total: ${meta?.total ?? 0}`} size="small" />
                        <Chip
                            label={`Página: ${page}/${meta?.last_page ?? 1}`}
                            size="small"
                            variant="outlined"
                        />
                        <Pagination
                            count={meta?.last_page ?? 1}
                            page={page}
                            onChange={handleChangePage}
                            size="small"
                        />
                        <Button size="small" onClick={clearFilters}>
                            Limpiar filtros
                        </Button>
                    </Box>

                    {admin && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCreate(true)}
                            size="small"
                            sx={{
                                height: '34px',
                                padding: '0 16px',
                                borderRadius: '12px',
                                fontWeight: 900,
                                letterSpacing: '0.4px',
                                textTransform: 'uppercase',
                                background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                boxShadow: '0 6px 14px rgba(15, 34, 77, 0.22)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                },
                            }}
                        >
                            Nueva factura
                        </Button>
                    )}
                </Box>
            </FiltersCard>

            <SectionTitle>
                <span>Listado de facturas</span>
            </SectionTitle>

            <ListContainer>
                {load ? (
                    [1, 2, 3].map((i) => (
                        <SkeletonRow key={i}>
                            <Skeleton variant="rectangular" width="100%" height={86} sx={{ borderRadius: 2 }} />
                        </SkeletonRow>
                    ))
                ) : invoices?.length ? (
                    invoices.map((inv) => (
                        <InvoiceCard key={inv.uuid} invoice={inv} onChanged={() => loadData(page, perPage)} />
                    ))
                ) : (
                    <EmptyState>
                        <FilterAltOutlinedIcon />
                        <Typography className="empty-title">No hay facturas</Typography>
                        <Typography className="empty-text">
                            No se encontraron facturas con los filtros actuales.
                        </Typography>
                    </EmptyState>
                )}
            </ListContainer>

            <CreateInvoiceModal
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onCreated={() => loadData(page, perPage)}
            />
        </PageContainer>
    );
};

export default Incomes;

/* ================= STYLES ================= */

const PageContainer = styled(Container)`
    padding: 0.8rem !important;
`;

const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.8rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }

    .title {
        color: #10285f;
        font-size: 1.2rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.82rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const FiltersCard = styled.div`
    padding: 0.6rem;
    border-radius: 14px;
    background: #fff;
    margin-bottom: 0.8rem;
    border: 1px solid rgba(15, 34, 77, 0.08);

    .filters-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.55rem;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .filters-info {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
    }

    @media (max-width: 700px) {
        .filters-header {
            flex-direction: column;
            align-items: stretch;
        }

        .filters-header .MuiButton-contained {
            width: 100%;
            height: 42px;
        }
    }
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.45rem;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTitle = styled.div`
    text-align: center;
    margin: 0.6rem 0;
    font-weight: 900;
    color: #10285f;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.85rem;

    span {
        padding: 0.3rem 0.9rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }
`;

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SkeletonRow = styled.div`
    width: 100%;
`;

const EmptyState = styled.div`
    min-height: 220px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    svg {
        font-size: 2.2rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.1rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        max-width: 340px;
        font-size: 0.86rem;
    }
`;
